import React, { useState, useEffect } from 'react';
import { Crown, Shield, Star, Network } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './LeaderboardPage.css';
import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import { useTranslation } from 'react-i18next';

const LeaderboardPage = () => {
    const { db, getDoc, doc, handleUserSelect, currentUserProfile } = useAppContext();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('Total Badges');
    const [scope, setScope] = useState('Global');
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            if (!db) return;
            setIsLoading(true);

            let leaderboardId;
            switch (activeTab) {
                case 'Total Badges': leaderboardId = 'total_badges'; break;
                case 'Rarity Score': leaderboardId = 'rarity_score'; break;
                case 'Constellation Level': leaderboardId = 'constellation_level'; break;
                default: leaderboardId = 'total_badges';
            }
            
            try {
                const leaderboardRef = doc(db, `artifacts/whispers-of-harmony/public/data/leaderboards`, leaderboardId);
                const docSnap = await getDoc(leaderboardRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const rankedData = data.rankings.map((user, index) => ({ ...user, rank: index + 1 }));
                    setLeaderboardData(rankedData);
                    setLastUpdated(data.lastUpdated?.toDate());
                } else {
                    setLeaderboardData([]);
                }
            } catch (error) {
                console.error("Error fetching leaderboard:", error);
                setLeaderboardData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLeaderboard();
    }, [activeTab, scope, db, getDoc, doc]);

    const renderRank = (rank) => {
        if (rank === 1) return <Crown className="rank-icon gold" />;
        if (rank === 2) return <Crown className="rank-icon silver" />;
        if (rank === 3) return <Crown className="rank-icon bronze" />;
        return <span className="rank-number">{rank}</span>;
    };

    const renderValueIcon = () => {
        switch (activeTab) {
            case 'Rarity Score': return <Star className="value-icon" />;
            case 'Constellation Level': return <Network className="value-icon" />;
            default: return <Shield className="value-icon" />;
        }
    };

    const myRankData = leaderboardData.find(u => u.userId === currentUserProfile?.id);
    const isMeInView = myRankData && myRankData.rank <= 20; 

    return (
        <div className="leaderboard-container">
            <motion.h1 
                className="leaderboard-title"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                {t('leaderboardPage.title')}
            </motion.h1>
            
            <div className="leaderboard-controls">
                <div className="tabs">
                    <button className={activeTab === 'Total Badges' ? 'active' : ''} onClick={() => setActiveTab('Total Badges')}>{t('leaderboardPage.tabs.totalBadges')}</button>
                    <button className={activeTab === 'Rarity Score' ? 'active' : ''} onClick={() => setActiveTab('Rarity Score')}>{t('leaderboardPage.tabs.rarityScore')}</button>
                    <button className={activeTab === 'Constellation Level' ? 'active' : ''} onClick={() => setActiveTab('Constellation Level')}>{t('leaderboardPage.tabs.constellationLevel', 'Constellation Level')}</button>
                </div>
                <div className="scope-toggle">
                    <button className={scope === 'Global' ? 'active' : ''} onClick={() => setScope('Global')}>{t('leaderboardPage.scope.global')}</button>
                    <button className={scope === 'Friends' ? 'active' : ''} onClick={() => setScope('Friends')} disabled>{t('leaderboardPage.scope.friends')}</button>
                </div>
            </div>

            {isLoading ? (
                <LoadingSpinner message={t('leaderboardPage.loading')} />
            ) : (
                <>
                    <div className="leaderboard-list">
                        <AnimatePresence>
                            {leaderboardData.length > 0 ? leaderboardData.map((user, index) => (
                                <motion.div 
                                    key={user.userId} 
                                    className={`leaderboard-item rank-${user.rank}`}
                                    onClick={() => handleUserSelect(user.userId)}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    whileHover={{ scale: 1.02 }}
                                >
                                    <div className="leaderboard-rank">{renderRank(user.rank)}</div>
                                    <img src={user.photoURL || 'https://placehold.co/40x40/374151/FFFFFF?text=U'} alt={user.displayName} className="leaderboard-avatar" />
                                    <div className="leaderboard-name">{user.displayName}</div>
                                    <div className="leaderboard-value">
                                        {renderValueIcon()}
                                        {user.value.toLocaleString()}
                                    </div>
                                </motion.div>
                            )) : (
                                <p className="text-center text-gray-400">{t('leaderboardPage.noData')}</p>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Sticky User Rank if not visible */}
                    {myRankData && !isMeInView && (
                        <motion.div 
                            className="leaderboard-item my-rank-sticky"
                            initial={{ y: 100 }}
                            animate={{ y: 0 }}
                        >
                            <div className="leaderboard-rank">{myRankData.rank}</div>
                            <img src={myRankData.photoURL} alt={myRankData.displayName} className="leaderboard-avatar" />
                            <div className="leaderboard-name text-yellow-400">{t('leaderboardPage.you')}</div>
                            <div className="leaderboard-value">
                                {renderValueIcon()}
                                {myRankData.value.toLocaleString()}
                            </div>
                        </motion.div>
                    )}

                    {lastUpdated && <p className="last-updated">{t('leaderboardPage.lastUpdated', { date: lastUpdated.toLocaleString() })}</p>}
                </>
            )}
        </div>
    );
};

export default LeaderboardPage;